// ConfettiManager.ts
interface Vector {
    x: number;
    y: number;
}

export interface ConfettiConfig {
    confettiesNumber?: number;
    confettiRadius?: number;
    confettiColors?: string[];
    emojies?: string[];
    svgIcon?: string | null;
}

interface ConfettiConstructorArgs {
    initialPosition: Vector;
    direction: "left" | "right";
    radius: number;
    colors: string[];
    emojis: string[];
    svgIcon: string | null;
}

const Utils = {
    getRandomInRange: (min: number, max: number, precision = 0) => {
        const multiplier = Math.pow(10, precision);
        const value = Math.random() * (max - min) + min;
        return Math.floor(value * multiplier) / multiplier;
    },
    getRandomItem: <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)],
    getScaleFactor: () => Math.log(window.innerWidth) / Math.log(1920),
    debounce: <T extends (...args: any[]) => void>(func: T, delay: number) => {
        let timeout: number | undefined;
        return (...args: Parameters<T>) => {
            if (timeout) clearTimeout(timeout);
            timeout = window.setTimeout(() => func(...args), delay);
        };
    },
};

const DEG_TO_RAD = Math.PI / 180;

const defaultConfettiConfig: Required<ConfettiConfig> = {
    confettiesNumber: 250,
    confettiRadius: 6,
    confettiColors: ["#fcf403","#62fc03","#f4fc03","#03e7fc","#03fca5","#a503fc","#fc03ad","#fc03c2"],
    emojies: [],
    svgIcon: null,
};

class Confetti {
    position: Vector;
    initialPosition: Vector;
    speed: Vector;
    finalSpeedX: number;
    rotationSpeed: number;
    dragCoefficient: number;
    radius: Vector;
    initialRadius: number;
    rotationAngle: number;
    emojiRotationAngle: number;
    radiusYDirection: "up" | "down";
    absCos: number;
    absSin: number;
    color: string | null;
    emoji: string | null;
    svgIcon: HTMLImageElement | null = null;
    createdAt: number;
    direction: "left" | "right";

    constructor({ initialPosition, direction, radius, colors, emojis, svgIcon }: ConfettiConstructorArgs) {
        const speedFactor = Utils.getRandomInRange(0.9, 1.7, 3) * Utils.getScaleFactor();
        this.speed = { x: speedFactor, y: speedFactor };
        this.finalSpeedX = Utils.getRandomInRange(0.2, 0.6, 3);
        this.rotationSpeed = emojis.length || svgIcon ? 0.01 : Utils.getRandomInRange(0.03, 0.07, 3) * Utils.getScaleFactor();
        this.dragCoefficient = Utils.getRandomInRange(0.0005, 0.0009, 6);
        this.radius = { x: radius, y: radius };
        this.initialRadius = radius;
        this.rotationAngle = direction === "left" ? Utils.getRandomInRange(0, 0.2, 3) : Utils.getRandomInRange(-0.2, 0, 3);
        this.emojiRotationAngle = Utils.getRandomInRange(0, 2 * Math.PI);
        this.radiusYDirection = "down";

        const angle = direction === "left" ? Utils.getRandomInRange(82, 15) * DEG_TO_RAD : Utils.getRandomInRange(-15, -82) * DEG_TO_RAD;
        this.absCos = Math.abs(Math.cos(angle));
        this.absSin = Math.abs(Math.sin(angle));

        const offset = Utils.getRandomInRange(-150, 0);
        this.position = {
            x: initialPosition.x + (direction === "left" ? -offset : offset) * this.absCos,
            y: initialPosition.y - offset * this.absSin,
        };
        this.initialPosition = { ...this.position };
        this.color = emojis.length || svgIcon ? null : Utils.getRandomItem(colors);
        this.emoji = emojis.length ? Utils.getRandomItem(emojis) : null;

        if (svgIcon) {
            const img = new Image();
            img.src = svgIcon;
            img.onload = () => this.svgIcon = img;
        }

        this.createdAt = Date.now();
        this.direction = direction;
    }

    draw(ctx: CanvasRenderingContext2D) {
        const { x, y } = this.position;
        const { x: rx, y: ry } = this.radius;
        const scale = window.devicePixelRatio;

        if (this.svgIcon) {
            ctx.save();
            ctx.translate(scale * x, scale * y);
            ctx.rotate(this.emojiRotationAngle);
            ctx.drawImage(this.svgIcon, -rx, -ry, rx*2, ry*2);
            ctx.restore();
        } else if (this.color) {
            ctx.fillStyle = this.color;
            ctx.beginPath();
            ctx.ellipse(x*scale, y*scale, rx*scale, ry*scale, this.rotationAngle, 0, 2*Math.PI);
            ctx.fill();
        } else if (this.emoji) {
            ctx.save();
            ctx.font = `${rx*scale}px serif`;
            ctx.translate(scale*x, scale*y);
            ctx.rotate(this.emojiRotationAngle);
            ctx.textAlign = "center";
            ctx.fillText(this.emoji, 0, ry/2);
            ctx.restore();
        }
    }

    updatePosition(deltaTime: number, now: number) {
        const elapsed = now - this.createdAt;
        if (this.speed.x > this.finalSpeedX) this.speed.x -= this.dragCoefficient*deltaTime;
        this.position.x += this.speed.x * (this.direction === "left" ? -this.absCos : this.absCos) * deltaTime;
        this.position.y = this.initialPosition.y - this.speed.y*this.absSin*elapsed + 0.00125*Math.pow(elapsed,2)/2;

        if(!this.emoji && !this.svgIcon){
            this.rotationSpeed = Math.max(this.rotationSpeed - 1e-5*deltaTime, 0);
            if(this.radiusYDirection==='down'){
                this.radius.y -= deltaTime*this.rotationSpeed;
                if(this.radius.y<=0){ this.radius.y=0; this.radiusYDirection='up'; }
            } else {
                this.radius.y += deltaTime*this.rotationSpeed;
                if(this.radius.y>=this.initialRadius){ this.radius.y=this.initialRadius; this.radiusYDirection='down'; }
            }
        }
    }

    isVisible(canvasHeight:number){
        return this.position.y < canvasHeight + 100;
    }
}

export class ConfettiManager {
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
    confetti: Confetti[] = [];
    lastUpdated = Date.now();

    constructor() {
        this.canvas = document.createElement("canvas");
        this.canvas.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;z-index:1000;pointer-events:none;";
        document.body.appendChild(this.canvas);

        const ctx = this.canvas.getContext("2d");
        if(!ctx) throw new Error("Canvas context not available");
        this.context = ctx;

        window.addEventListener("resize", Utils.debounce(()=>this.resizeCanvas(),200));
        this.resizeCanvas();
        requestAnimationFrame(()=>this.loop());
    }

    resizeCanvas(){
        this.canvas.width = window.innerWidth*window.devicePixelRatio;
        this.canvas.height = window.innerHeight*window.devicePixelRatio;
    }

    addConfetti(config: ConfettiConfig = {}){
        const { confettiesNumber, confettiRadius, confettiColors, emojies, svgIcon } = {...defaultConfettiConfig,...config};
        const baseY = (5*window.innerHeight)/7;

        for(let i=0;i<confettiesNumber/2;i++){
            this.confetti.push(
                new Confetti({initialPosition:{x:0,y:baseY},direction:"right",radius:confettiRadius,colors:confettiColors,emojis:emojies,svgIcon}),
                new Confetti({initialPosition:{x:window.innerWidth,y:baseY},direction:"left",radius:confettiRadius,colors:confettiColors,emojis:emojies,svgIcon})
            );
        }
    }

    loop(){
        const now = Date.now();
        const delta = now - this.lastUpdated;
        this.lastUpdated = now;

        this.context.clearRect(0,0,this.canvas.width,this.canvas.height);
        this.confetti = this.confetti.filter(c=>{
            c.updatePosition(delta,now);
            c.draw(this.context);
            return c.isVisible(this.canvas.height);
        });

        requestAnimationFrame(()=>this.loop());
    }
}

// Optional singleton
export const confettiManager = new ConfettiManager();
export const triggerConfetti = (config?: ConfettiConfig) => confettiManager.addConfetti(config);
